import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';
import type { QuestionBox, WorksheetQuestion } from '../lib/vision-types';
import { type AnswerPlacements, autoPlaceAnswers, drawWorksheet, loadImage } from '../lib/render';

// Drag-and-resize editor for answers on the worksheet.
//
// The worksheet (photo + answers) is drawn by the same drawWorksheet() the PDF export
// uses, so what the pupil sees while dragging is exactly what gets saved. On top of
// the canvas sits one invisible hit area per answer:
//   - tap an answer to select it
//   - drag it to move it
//   - pull the round corner handle to make it bigger or smaller (the text grows and
//     shrinks with it)
//   - Smaller / Bigger buttons do the same as the handle, always on screen (the
//     handle can be scrolled out of view when zoomed in)
//   - "Put it back" returns it to where the AI placed it
// Keyboard: arrow keys move the selected answer, Shift + arrows resize it.
//
// Positions are normalised (0-1) so they survive any display size or zoom level.

interface Props {
  image: string;
  questions: WorksheetQuestion[];
  answers: Record<string, string>;
  placements: AnswerPlacements;
  onPlace: (questionId: string, box: QuestionBox) => void;
  onReset: (questionId: string) => void;
}

type Mode = 'move' | 'resize';

interface Gesture {
  id: string;
  mode: Mode;
  startX: number;
  startY: number;
  start: QuestionBox;
  current: QuestionBox;
  layerW: number;
  layerH: number;
  moved: boolean;
}

const MIN_W = 0.04;
const MIN_H = 0.015;
const TAP_SLOP_PX = 6;
const KEY_STEP = 0.005;
const SCALE_STEP = 1.2;

export function AnswerPlacementEditor({ image, questions, answers, placements, onPlace, onReset }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [auto, setAuto] = useState<AnswerPlacements>({});
  const [live, setLive] = useState<{ id: string; box: QuestionBox } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState(false);

  // Load the photo and work out the automatic boxes once per worksheet.
  useEffect(() => {
    let cancelled = false;
    void loadImage(image).then((loaded) => {
      if (cancelled) return;
      setImg(loaded);
      setAuto(autoPlaceAnswers(loaded, questions));
    });
    return () => {
      cancelled = true;
    };
  }, [image, questions]);

  const userPlaced = useMemo<AnswerPlacements>(
    () => (live ? { ...placements, [live.id]: live.box } : placements),
    [placements, live],
  );
  const boxes = useMemo<AnswerPlacements>(() => ({ ...auto, ...userPlaced }), [auto, userPlaced]);
  const answered = useMemo(() => questions.filter((q) => (answers[q.id] ?? '').trim()), [questions, answers]);

  // Redraw on every change (including mid-drag) on the next animation frame.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    const frame = requestAnimationFrame(() => {
      if (canvas.width !== img.naturalWidth) canvas.width = img.naturalWidth;
      if (canvas.height !== img.naturalHeight) canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (ctx) drawWorksheet(ctx, img, questions, answers, boxes, {}, userPlaced);
    });
    return () => cancelAnimationFrame(frame);
  }, [img, questions, answers, boxes, userPlaced]);

  function beginGesture(e: PointerEvent<HTMLElement>, id: string, mode: Mode) {
    const layer = layerRef.current;
    const box = boxes[id];
    if (!layer || !box) return;
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = layer.getBoundingClientRect();
    gestureRef.current = {
      id,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      start: box,
      current: box,
      layerW: rect.width,
      layerH: rect.height,
      moved: false,
    };
    setSelected(id);
  }

  function updateGesture(e: PointerEvent<HTMLElement>) {
    const g = gestureRef.current;
    if (!g) return;
    const dxPx = e.clientX - g.startX;
    const dyPx = e.clientY - g.startY;
    if (!g.moved && Math.hypot(dxPx, dyPx) < TAP_SLOP_PX) return;
    g.moved = true;
    const dx = dxPx / g.layerW;
    const dy = dyPx / g.layerH;
    const b = g.start;
    const next =
      g.mode === 'move'
        ? { ...b, x: clamp(b.x + dx, 0, 1 - b.w), y: clamp(b.y + dy, 0, 1 - b.h) }
        : { ...b, w: clamp(b.w + dx, MIN_W, 1 - b.x), h: clamp(b.h + dy, MIN_H, 1 - b.y) };
    g.current = next;
    setLive({ id: g.id, box: next });
  }

  function endGesture() {
    const g = gestureRef.current;
    gestureRef.current = null;
    if (g?.moved) onPlace(g.id, g.current);
    setLive(null);
  }

  function onKey(e: KeyboardEvent<HTMLElement>, id: string) {
    const arrows: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const dir = arrows[e.key];
    const b = boxes[id];
    if (!dir || !b) return;
    e.preventDefault();
    const [dx, dy] = [dir[0] * KEY_STEP, dir[1] * KEY_STEP];
    onPlace(
      id,
      e.shiftKey
        ? { ...b, w: clamp(b.w + dx, MIN_W, 1 - b.x), h: clamp(b.h + dy, MIN_H, 1 - b.y) }
        : { ...b, x: clamp(b.x + dx, 0, 1 - b.w), y: clamp(b.y + dy, 0, 1 - b.h) },
    );
  }

  function scaleSelected(factor: number) {
    if (!selected) return;
    const b = boxes[selected];
    if (!b) return;
    // Scale both sides by the same amount so the text keeps its shape; stop at the
    // minimum size or the page edge rather than squashing one side.
    const f = Math.min(Math.max(factor, MIN_W / b.w, MIN_H / b.h), 1 / b.w, 1 / b.h);
    const w = b.w * f;
    const h = b.h * f;
    onPlace(selected, { x: clamp(b.x, 0, 1 - w), y: clamp(b.y, 0, 1 - h), w, h });
  }

  const selectedQ = answered.find((q) => q.id === selected) ?? null;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-ink/70 leading-relaxed">
        Tap an answer, then drag it to the right place. Use Bigger and Smaller, or pull the round handle, to
        change its size.
      </p>

      <div className="bg-white rounded-2xl border border-ink/10 p-2 shadow-sm">
        {/* p-5 leaves room for the resize handle when an answer sits on the page edge. */}
        <div className="overflow-auto max-h-[60vh] rounded-xl p-5">
          <div className="relative" style={{ width: zoomed ? '200%' : '100%' }}>
            <canvas ref={canvasRef} className="block w-full h-auto" aria-hidden="true" />
            <div
              ref={layerRef}
              className="absolute inset-0"
              onPointerDown={(e) => {
                if (e.target === e.currentTarget) setSelected(null);
              }}
            >
              {answered.map((q) => {
                const b = boxes[q.id];
                if (!b) return null;
                const isSelected = q.id === selected;
                return (
                  <div
                    key={q.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`Answer ${q.number || q.id}: ${answers[q.id]}. Drag to move.`}
                    aria-pressed={isSelected}
                    onPointerDown={(e) => beginGesture(e, q.id, 'move')}
                    onPointerMove={updateGesture}
                    onPointerUp={endGesture}
                    onPointerCancel={endGesture}
                    onFocus={() => setSelected(q.id)}
                    onKeyDown={(e) => onKey(e, q.id)}
                    className={`absolute rounded-md cursor-move outline-none transition-shadow ${
                      isSelected
                        ? 'ring-2 ring-accent bg-accent/10 shadow-lg shadow-accent/20'
                        : 'border border-dashed border-accent/50 hover:border-accent'
                    }`}
                    style={{
                      left: `${b.x * 100}%`,
                      top: `${b.y * 100}%`,
                      width: `${b.w * 100}%`,
                      height: `${b.h * 100}%`,
                      touchAction: 'none',
                      zIndex: isSelected ? 10 : 1,
                    }}
                  >
                    {/* Invisible finger-sized hit area: at least 44px each way, centred on small answers. */}
                    <span
                      aria-hidden="true"
                      className="absolute"
                      style={{
                        top: 'min(0px, calc((100% - 44px) / 2))',
                        bottom: 'min(0px, calc((100% - 44px) / 2))',
                        left: 'min(0px, calc((100% - 44px) / 2))',
                        right: 'min(0px, calc((100% - 44px) / 2))',
                      }}
                    />
                    {isSelected && (
                      <span
                        role="button"
                        aria-label="Drag to resize"
                        onPointerDown={(e) => beginGesture(e, q.id, 'resize')}
                        onPointerMove={updateGesture}
                        onPointerUp={endGesture}
                        onPointerCancel={endGesture}
                        className="absolute z-10 right-0 bottom-0 translate-x-1/2 translate-y-1/2 w-9 h-9 rounded-full bg-accent border-[3px] border-white shadow-md cursor-nwse-resize grid place-items-center"
                        style={{ touchAction: 'none' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                          <path d="M4 10L10 4M7 11L11 7" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {selectedQ && (
        <div className="flex items-center gap-2" role="group" aria-label="Selected answer">
          <button onClick={() => scaleSelected(1 / SCALE_STEP)} className={TOOL_BTN}>
            Smaller
          </button>
          <button onClick={() => scaleSelected(SCALE_STEP)} className={TOOL_BTN}>
            Bigger
          </button>
          {placements[selectedQ.id] && (
            <button onClick={() => onReset(selectedQ.id)} className={`ml-auto ${RESET_BTN}`}>
              Put it back
            </button>
          )}
        </div>
      )}

      <div>
        <button onClick={() => setZoomed((z) => !z)} className={TOOL_BTN}>
          {zoomed ? 'Zoom out' : 'Zoom in'}
        </button>
      </div>
    </div>
  );
}

const TOOL_BTN =
  'px-4 py-2 rounded-full text-sm font-medium text-accent border border-accent/30 hover:bg-accentSoft transition';

const RESET_BTN =
  'px-4 py-2 rounded-full text-sm font-medium text-ink/70 border border-ink/15 hover:bg-ink/5 transition';

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
